// Dispatches a map node to its appropriate scene.
// Combat nodes → build enemies → TeamHubScene (pre-combat planning) → CombatScene.
// Non-combat nodes → their own scenes.

import type { Game } from "../game/Game";
import type { MapNode } from "../map/CampaignMap";
import { RecruitPickScene } from "./RecruitPickScene";
import { randomChampionOffer } from "./rewards/ChampionRewards";
import { RewardScene } from "./RewardScene";
import { randomItemReward } from "./rewards/ItemRewards";
import { ItemPickScene } from "./ItemPickScene";
import type { ItemId } from "../data/items";
import { MapScene } from "./MapScene";
import { HealScene } from "./HealScene";
import { MinionPickScene } from "./MinionPickScene";
import { BuffPickScene } from "./BuffPickScene";
import { MainMenuScene } from "./MainMenuScene";
import { WorldMapScene } from "./WorldMapScene";
import { VictoryScene } from "./VictoryScene";
import { ACTS, getAct } from "../data/acts";
import { generateAct } from "../map/MapGenerator";
import type { ChampionRegion } from "../data/champions";
import { buildChampionEnemyUnit, buildEnemyUnit } from "../combat/UnitFactory";
import type { EnemyId } from "../data/enemies";
import { CHAMPIONS, type ChampionId, type ChampionTier } from "../data/champions";
import { XP_FOR_LEVEL, JUNGLE_XP_PER_LAYER, levelForXp, MAX_LEVEL } from "../units/UnitStats";
import { saveRun } from "../game/RunState";
import { RNG, randomSeed } from "../game/RNG";
import type { Unit } from "../units/Unit";
import type { NodeType } from "../data/nodes";
import { NODE_TYPES } from "../data/nodes";
import { CombatScene } from "./CombatScene";
import type { PendingCombat } from "../game/RunState";
import type { Difficulty } from "../data/difficulty";
import { getBossComp } from "../data/bossComps";

// Melee (range <= 1) go to front slots; ranged go to back slots.
function assignSmartSlots(units: Unit[]): void {
  const melee = units.filter((u) => u.def.range <= 1);
  const ranged = units.filter((u) => u.def.range > 1);
  // Front slots: 0 for melee; back slots: 2, 1 for ranged; fill remaining from front.
  const order = [...melee, ...ranged];
  for (let i = 0; i < order.length && i < 3; i++) {
    order[i].pos = { q: i, r: 1 };
  }
}

export const LAYERS_PER_ACT = 5;

// Enemy level tracks the player's expected level from the XP curve.
// 30 jungle camps across 6 acts × 5 layers each.
export function enemyLevelForLayer(actNumber: number, layer: number): number {
  const globalLayer = (actNumber - 1) * LAYERS_PER_ACT + layer;
  return Math.max(1, levelForXp(globalLayer * JUNGLE_XP_PER_LAYER));
}

export function enterNode(game: Game, node: MapNode): void {
  let type = node.type;
  if (node.type === "randomEvent") {
    const state = game.runState!;
    if (state.pendingRandomEventNodeId === node.id && state.pendingRandomEventType) {
      type = state.pendingRandomEventType as typeof type;
    } else {
      type = rollRandomEventType(game);
      state.pendingRandomEventNodeId = node.id;
      state.pendingRandomEventType = type;
      saveRun(state);
    }
  }

  // Boss nodes are now regular combat encounters using the current region's champion pool.
  // After victory, routeReward sends the player to RegionCompleteScene.

  // Non-combat special nodes
  switch (type) {
    case "recruit": {
      const state = game.runState!;
      if (!state.pendingRecruitOffers) {
        state.pendingRecruitOffers = randomChampionOffer(game, 3);
        saveRun(state);
      }
      game.sceneManager.transition(new RecruitPickScene(game, node, state.pendingRecruitOffers));
      return;
    }
    case "item": {
      const state = game.runState!;
      if (!state.pendingItemOffers) {
        state.pendingItemOffers = randomItemReward(game.rng, 3) as string[];
        saveRun(state);
      }
      game.sceneManager.transition(new ItemPickScene(game, node, state.pendingItemOffers as ItemId[]));
      return;
    }
    case "heal": {
      game.runState!.map?.markComplete(node.id);
      game.sceneManager.transition(new HealScene(game, node));
      return;
    }
    case "minion": {
      const { enemies, enemyChampionIds, fighterLimit: buildLimit } = buildEnemiesForNode(game, node, type);
      const nodeDef = NODE_TYPES["minion"];
      const fighterLimit = buildLimit ?? nodeDef.fighterLimit;
      const pending: PendingCombat = { node: { ...node, type }, enemies, fighterLimit, enemyChampionIds };
      const state = game.runState!;
      state.pendingCombat = pending;
      state.combatFighterIds = null;
      game.sceneManager.transition(new MinionPickScene(game, pending.node));
      return;
    }
  }

  // Combat nodes
  const nodeDef = NODE_TYPES[type as keyof typeof NODE_TYPES];
  if (nodeDef?.isCombat) {
    const { enemies, enemyChampionIds, fighterLimit: buildLimit } = buildEnemiesForNode(game, node, type);
    const fighterLimit = buildLimit ?? nodeDef.fighterLimit;
    const pending: PendingCombat = { node: { ...node, type }, enemies, fighterLimit, enemyChampionIds };
    const state = game.runState!;
    state.pendingCombat = pending;
    state.combatFighterIds = null;
    game.sceneManager.transition(new CombatScene(game, pending.node));
    return;
  }

  game.sceneManager.transition(new MapScene(game));
}

interface EnemyBuild {
  enemies: Unit[];
  enemyChampionIds: ChampionId[];
  fighterLimit?: number | null;
}

function buildEnemiesForNode(game: Game, node: MapNode, type: NodeType): EnemyBuild {
  const state = game.runState;
  const actNumber = state?.act ?? 1;
  const act = getAct(actNumber);
  const enemyLevel = Math.max(1, enemyLevelForLayer(actNumber, node.layer) - 2 + (act.levelOffset ?? 0));

  switch (type) {
    case "minion": {
      return buildMinionWave(enemyLevel);
    }
    case "boss": {
      return buildFixedBossTeam(act.region, actNumber, state?.difficulty ?? "normal");
    }
    case "jungle": {
      return buildJungleCamp(node.campType ?? "gromp", enemyLevel);
    }
    case "redBuff": {
      return { enemies: [buildEnemyUnit("redBuff", 0, enemyLevel + 2)], enemyChampionIds: [], fighterLimit: null };
    }
    case "blueBuff": {
      return { enemies: [buildEnemyUnit("blueBuff", 0, enemyLevel + 2)], enemyChampionIds: [], fighterLimit: null };
    }
    default:
      return { enemies: [], enemyChampionIds: [] };
  }
}

function buildFixedBossTeam(region: ChampionRegion, actNumber: number, difficulty: Difficulty): EnemyBuild {
  const comp = getBossComp(difficulty, actNumber);
  if (!comp || comp.length === 0) {
    // Safety fallback: random region pool at level 1 / 1-star.
    const pool = (Object.keys(CHAMPIONS) as ChampionId[]).filter(id => CHAMPIONS[id].region === region);
    const units = pool.map(id => buildChampionEnemyUnit(id, 0, 1, 1));
    assignSmartSlots(units);
    return { enemies: units, enemyChampionIds: pool };
  }
  const units = comp.map(({ id, level, stars }) =>
    buildChampionEnemyUnit(id, 0, Math.max(1, Math.min(MAX_LEVEL, level)), stars),
  );
  assignSmartSlots(units);
  return { enemies: units, enemyChampionIds: comp.map(c => c.id) };
}

// Build a team with exact tier counts (for boss and tournament fights).
export function buildExplicitTeam(rng: RNG, tierCounts: Partial<Record<ChampionTier, number>>, level: number): EnemyBuild {
  const byTier: Partial<Record<ChampionTier, ChampionId[]>> = {};
  for (const id of Object.keys(CHAMPIONS) as ChampionId[]) {
    (byTier[CHAMPIONS[id].tier] ??= []).push(id);
  }

  const used = new Set<ChampionId>();
  const picked: ChampionId[] = [];

  for (const [tier, count] of Object.entries(tierCounts) as [ChampionTier, number][]) {
    const pool = [...(byTier[tier] ?? [])].filter((id) => !used.has(id));
    rng.shuffle(pool);
    for (let i = 0; i < count && i < pool.length; i++) {
      picked.push(pool[i]);
      used.add(pool[i]);
    }
  }

  const units = picked.map((id) => buildChampionEnemyUnit(id, 0, level));
  assignSmartSlots(units);
  return { enemies: units, enemyChampionIds: picked };
}

const JUNGLE_CAMP_DEFS: Record<string, { enemyId: EnemyId; count: number }> = {
  gromp:   { enemyId: "gromp",   count: 1 },
  wolf:    { enemyId: "wolf",    count: 3 },
  raptor:  { enemyId: "raptor",  count: 5 },
  krug:    { enemyId: "krug",    count: 8 },
  scuttle: { enemyId: "scuttle", count: 1 },
};

function buildMinionWave(baseLevel: number): EnemyBuild {
  const enemies: Unit[] = [];
  for (let slot = 0; slot < 2; slot++) {
    enemies.push(buildEnemyUnit("minion", slot, baseLevel));
  }
  enemies.push(buildEnemyUnit("casterMinion", 2, baseLevel));
  return { enemies, enemyChampionIds: [], fighterLimit: 1 };
}

function buildJungleCamp(campType: string, baseLevel: number): EnemyBuild {
  const def = JUNGLE_CAMP_DEFS[campType] ?? JUNGLE_CAMP_DEFS.gromp;
  const enemies: Unit[] = [];
  for (let slot = 0; slot < def.count && slot < 3; slot++) {
    enemies.push(buildEnemyUnit(def.enemyId, slot, baseLevel));
  }
  return { enemies, enemyChampionIds: [], fighterLimit: 1 };
}

function rollRandomEventType(game: Game): NodeType {
  const weights: [NodeType, number][] = [
    ["minion",  30],
    ["jungle",  30],
    ["recruit", 25],
    ["redBuff",  8],
    ["blueBuff", 7],
  ];
  const total = weights.reduce((s, [, w]) => s + w, 0);
  let r = game.rng.next() * total;
  for (const [t, w] of weights) {
    r -= w;
    if (r <= 0) return t;
  }
  return "minion";
}

/** XP a newly recruited champion starts with — 2 levels below the current enemy curve. */
export function recruitStartXp(game: Game, node: MapNode): number {
  const actNumber = game.runState?.act ?? 1;
  const targetLevel = Math.max(1, enemyLevelForLayer(actNumber, node.layer) - 2);
  return XP_FOR_LEVEL[Math.min(targetLevel - 1, XP_FOR_LEVEL.length - 1)];
}

export function routeReward(game: Game, node: MapNode): void {
  const state = game.runState;
  if (!state) {
    game.sceneManager.transition(new MainMenuScene(game));
    return;
  }
  const nodeDef = NODE_TYPES[node.type as keyof typeof NODE_TYPES];
  if (nodeDef?.isCombat) state.combatsCompleted++;
  state.map?.markComplete(node.id);
  state.pendingCombat = null;

  // Boss victory → heal, advance act, show journey map (or victory screen).
  if (node.type === "boss") {
    state.healFull();
    game.achievements.onActComplete(state.act, state.roster, state.difficulty);
    if (state.act >= ACTS.length) {
      game.sceneManager.transition(new VictoryScene(game));
    } else {
      state.act++;
      state.map = generateAct(state.act, state.seed);
      game.sceneManager.transition(new WorldMapScene(game));
    }
    return;
  }

  // Buff camp victory → let player choose which champion receives the buff
  if (node.type === "redBuff" || node.type === "blueBuff") {
    game.sceneManager.transition(new BuffPickScene(game, node, node.type));
    return;
  }

  game.sceneManager.transition(new RewardScene(game, node));
}
